import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Star } from "lucide-react";
import {
  adminListServices,
  adminCreateService,
  adminUpdateService,
  adminDeleteService,
} from "../lib/servicesApi";

const BLANK = {
  slug: "",
  title: "",
  tag: "",
  tier: "",
  tagline: "",
  short: "",
  features: [],
  turnaround: "",
  price: null,
  currency: null,
  price_id: null,
  featured: false,
  order: 100,
  active: true,
};

export default function AdminServices() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // service being edited or "new"
  const [busy, setBusy] = useState({});

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const list = await adminListServices(username, password);
      setServices(list);
      setAuthed(true);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid credentials." : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const list = await adminListServices(username, password);
      setServices(list);
    } catch {
      toast.error("Failed to refresh.");
    }
  };

  const save = async (payload) => {
    try {
      if (payload.id) {
        await adminUpdateService(username, password, payload.id, payload);
        toast.success("Service updated.");
      } else {
        await adminCreateService(username, password, payload);
        toast.success("Service created.");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.title}"?`)) return;
    setBusy((b) => ({ ...b, [svc.id]: true }));
    try {
      await adminDeleteService(username, password, svc.id);
      setServices((prev) => prev.filter((s) => s.id !== svc.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy((b) => {
        const { [svc.id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const toggleField = async (svc, field) => {
    setBusy((b) => ({ ...b, [svc.id]: true }));
    try {
      await adminUpdateService(username, password, svc.id, { [field]: !svc[field] });
      refresh();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy((b) => {
        const { [svc.id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="admin-services-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-center gap-6 text-[11px] tracking-[0.3em] uppercase text-white/60">
          <Link to="/" className="hover:text-[#2A5BFF]">← Back</Link>
          <Link to="/admin" className="hover:text-[#2A5BFF]" data-testid="admin-services-waitlist-link">Waitlist Admin</Link>
        </div>
        <h1 className="font-anton uppercase text-5xl md:text-6xl mt-8">Admin — Services</h1>

        {!authed && (
          <form onSubmit={load} className="mt-10 max-w-md border border-white/10 p-6" data-testid="admin-services-login">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Username</label>
            <input
              className="input-line focus-ring"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              data-testid="admin-services-username"
              required
            />
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
            <input
              type="password"
              className="input-line focus-ring"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-services-password"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
              data-testid="admin-services-login-button"
            >
              {loading ? "…" : "Sign In"}
            </button>
          </form>
        )}

        {authed && (
          <div className="mt-10" data-testid="admin-services-content">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-[11px] tracking-[0.28em] uppercase text-white/50">
                {services.length} services
              </div>
              <button
                onClick={() => setEditing({ ...BLANK })}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase"
                data-testid="admin-services-new"
              >
                <Plus size={14} /> New Service
              </button>
            </div>

            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.28em] uppercase text-white/40 border-b border-white/10">
                  <tr>
                    <th className="p-4 text-left">Title</th>
                    <th className="p-4 text-left">Slug</th>
                    <th className="p-4 text-left">Turnaround</th>
                    <th className="p-4 text-left">Price</th>
                    <th className="p-4 text-center">Featured</th>
                    <th className="p-4 text-center">Active</th>
                    <th className="p-4 text-center">Order</th>
                    <th className="p-4 text-right w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-service-row-${s.slug}`}>
                      <td className="p-4">{s.title}</td>
                      <td className="p-4 text-white/60">{s.slug}</td>
                      <td className="p-4 text-white/70">{s.turnaround || "—"}</td>
                      <td className="p-4 text-white/70">{s.price != null ? `${s.currency || "$"}${s.price}` : "—"}</td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleField(s, "featured")}
                          disabled={!!busy[s.id]}
                          className={`inline-flex items-center justify-center w-8 h-8 border transition-colors ${s.featured ? "border-[#2A5BFF] text-[#2A5BFF]" : "border-white/15 text-white/40 hover:border-[#2A5BFF]"}`}
                          aria-label="Toggle featured"
                          data-testid={`admin-service-featured-${s.slug}`}
                        >
                          <Star size={14} fill={s.featured ? "currentColor" : "none"} />
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleField(s, "active")}
                          disabled={!!busy[s.id]}
                          className={`text-[10px] tracking-[0.22em] uppercase px-2 py-1 border ${s.active ? "border-[#2A5BFF] text-[#2A5BFF]" : "border-white/20 text-white/50"}`}
                          data-testid={`admin-service-active-${s.slug}`}
                        >
                          {s.active ? "Live" : "Hidden"}
                        </button>
                      </td>
                      <td className="p-4 text-center text-white/50">{s.order}</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => setEditing({ ...s })}
                            className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-[#2A5BFF] hover:text-[#2A5BFF] transition-colors"
                            aria-label={`Edit ${s.title}`}
                            data-testid={`admin-service-edit-${s.slug}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => remove(s)}
                            disabled={!!busy[s.id]}
                            className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-red-500 hover:text-red-500 transition-colors"
                            aria-label={`Delete ${s.title}`}
                            data-testid={`admin-service-delete-${s.slug}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {services.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-white/40 text-sm">No services yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <ServiceEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

// ------------------------ Editor Drawer ------------------------
function ServiceEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    features: Array.isArray(initial.features) ? initial.features.join("\n") : "",
    price: initial.price ?? "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      features: form.features
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      price: form.price === "" || form.price == null ? null : Number(form.price),
      order: Number(form.order) || 100,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4" onClick={onClose} data-testid="admin-service-editor">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#0A0A14] border border-white/10 flex flex-col max-h-[90vh]"
      >
        <div className="flex items-center justify-between px-6 md:px-8 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A14] z-10">
          <h2 className="font-anton uppercase text-2xl">
            {form.id ? "Edit Service" : "New Service"}
          </h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white text-sm" data-testid="admin-service-editor-cancel">Close</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} required testid="editor-title" />
          <Field label="Slug *" value={form.slug} onChange={(v) => set("slug", v)} required testid="editor-slug" />
          <Field label="Tag" value={form.tag} onChange={(v) => set("tag", v)} testid="editor-tag" />
          <Field label="Tier" value={form.tier} onChange={(v) => set("tier", v)} testid="editor-tier" />
          <Field label="Turnaround" value={form.turnaround} onChange={(v) => set("turnaround", v)} testid="editor-turnaround" placeholder="e.g. 24–48h" />
          <Field label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="editor-order" />
          <Field label="Price" type="number" step="0.01" value={form.price} onChange={(v) => set("price", v)} testid="editor-price" placeholder="Leave blank if not priced" />
          <Field label="Currency" value={form.currency || ""} onChange={(v) => set("currency", v || null)} testid="editor-currency" placeholder="e.g. £, $, €" />
        </div>

        <div className="mt-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Tagline</label>
          <input type="text" className="input-line focus-ring" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="editor-tagline" />
        </div>
        <div className="mt-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Short description</label>
          <textarea rows={3} className="input-line focus-ring resize-none" value={form.short || ""} onChange={(e) => set("short", e.target.value)} data-testid="editor-short" />
        </div>
        <div className="mt-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Features (one per line)</label>
          <textarea rows={6} className="input-line focus-ring resize-none" value={form.features} onChange={(e) => set("features", e.target.value)} data-testid="editor-features" />
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="editor-featured" />
            Featured
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} data-testid="editor-active" />
            Active (visible on site)
          </label>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button type="submit" className="btn-primary px-6 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="admin-service-editor-save">
            {form.id ? "Save Changes" : "Create Service"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost px-6 py-3 text-[11px] tracking-[0.24em] uppercase">
            Cancel
          </button>
        </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder, step }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">{label}</label>
      <input
        type={type}
        step={step}
        className="input-line focus-ring"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
